import React, { useRef, useEffect, useState } from "react";
import "./Chatbot.css";
import oscarLogo from "../../images/Oscar.svg";
import { useDispatch } from "react-redux";
import { chatbotPostApi } from "../../Services/ChatBotApi/chatbotapi";
import GoogleAd from "../../Utils/GoogleAd";
import { predefinedResponse } from "../../Utils/Constant";
import { StingKillInfo } from "../../Utils/LetterCase";

function Chatbot({
  setChatBotActive,
  communitName,
  DefaultQuestion,
  RealEstate,
  MarketInsights,
  MortgageSolutions,
  Insurancemadeeasy,
  setDefaultQuestion,
  setRealEstate,
  setMarketInsights,
  setMortgageSolutions,
  setInsurancemadeeasy,
}) {
  const [messages, setMessages] = React.useState([]);
  const [userInput, setUserInput] = React.useState("");
  const [isDisplayingMessage, setIsDisplayingMessage] = useState(false);
  const [letterIndex, setLetterIndex] = useState(0);
  const [currentMessage, setCurrentMessage] = useState("");
  const DefaultQuestionRef = useRef(false);
  const RealEstateResponseRef = useRef(false);
  const MarketInsightsResponseRef = useRef(false);
  const MortgageSolutionsResponseRef = useRef(false);
  const InsurancemadeeasyResponseRef = useRef(false);
  const dispatch = useDispatch();
  const messagesEndRef = useRef(null);
  const [randomId, setRandomId] = useState();
  const [type, setType] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]); // This will run every time messages change

  useEffect(() => {
    setRandomId(generateRandomNumber());
  }, []);

  // Add this useEffect for better scroll handling
  useEffect(() => {
    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    };

    // Scroll on new messages
    scrollToBottom();

    // Also scroll when messages are being typed out
    if (isDisplayingMessage) {
      scrollToBottom();
    }
  }, [messages, currentMessage, isDisplayingMessage]);

  useEffect(() => {
    const fetchResponse = (question) => {
      // Predefined responses based on the question
      const predefinedResponses = predefinedResponse;

      // Check if the question has a predefined response
      const responseText =
        predefinedResponses[question] ||
        "I'm sorry, I don't have information on that.";

      // Check if there is already a loading message before adding a new one
      const isAlreadyLoading = messages.some((msg) => msg.loading);

      if (!isAlreadyLoading) {
        setMessages((prev) => [
          ...prev,
          { type: "bot", text: "Loading...", loading: true },
        ]);
      }

      // Simulate delay for loading and then update the response
      setTimeout(() => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.loading
              ? { type: "bot", text: responseText, loading: false }
              : msg
          )
        );
        displayMessageLetterByLetter(responseText);
      }, 1000); // Simulate a delay of 1 second
    };

    if (DefaultQuestion && !DefaultQuestionRef.current) {
      DefaultQuestionRef.current = true;
      fetchResponse("Default Question?");
      setDefaultQuestion(false); // Reset the flag after calling the response
      setType("default");
    }

    if (RealEstate && !RealEstateResponseRef.current) {
      RealEstateResponseRef.current = true;
      fetchResponse("Real Estate information?");
      setRealEstate(false); // Reset the flag after calling the response
      setType("realestate");
    }

    if (MarketInsights && !MarketInsightsResponseRef.current) {
      MarketInsightsResponseRef.current = true;
      fetchResponse("Market Insights information?");
      setMarketInsights(false); // Reset the flag after calling the response
      setType("market");
    }

    if (MortgageSolutions && !MortgageSolutionsResponseRef.current) {
      MortgageSolutionsResponseRef.current = true;
      fetchResponse("Mortgage Solution information?");
      setMortgageSolutions(false); // Reset the flag after calling the response
      setType("mortgage_solutions");
    }

    if (Insurancemadeeasy && !InsurancemadeeasyResponseRef.current) {
      InsurancemadeeasyResponseRef.current = true;
      fetchResponse("Insurance made easy information?");
      setInsurancemadeeasy(false); // Reset the flag after calling the response
      setType("home_insurance");
    }
  }, [
    DefaultQuestion,
    RealEstate,
    MarketInsights,
    MortgageSolutions,
    Insurancemadeeasy,
    messages,
    setDefaultQuestion,
    setRealEstate,
    setMarketInsights,
    setMortgageSolutions,
    setInsurancemadeeasy,
  ]);

  useEffect(() => {
    if (
      messages.length > 0 &&
      !messages.some((msg) => msg.loading) &&
      textareaRef.current
    ) {
      textareaRef.current.focus();
    }
  }, [messages]);

  const handleClose = () => {
    setChatBotActive(false);
    setUserInput("");
    setMessages([]);
    setType("");
  };

  function generateRandomNumber() {
    const randomNumber = Math.random();
    const scaledNumber = randomNumber * 900000 + 100000;
    const sixDigitNumber = Math.floor(scaledNumber);
    return sixDigitNumber;
  }

  const handleSubmit = async (e) => {
    if (userInput.trim()) {
      const userMessage = { type: "user", text: userInput, loading: false };
      const botPlaceholder = { type: "bot", text: "", loading: true }; // Bot's loading message

      setMessages((prev) => [...prev, userMessage, botPlaceholder]);
      setUserInput("");

      try {
        const response = await dispatch(
          chatbotPostApi({
            question: userInput,
            session_id: randomId,
            community_name: communitName === null ? null : communitName, // Send null if communityName is null
            type: type,
          })
        ).unwrap();

        if (response.response_code === 200) {
          setMessages((prev) =>
            prev.map((msg, index) =>
              index === prev.length - 1 // Update the last bot message
                ? { type: "bot", text: response.result, loading: false }
                : msg
            )
          );
          displayMessageLetterByLetter(response.result);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setMessages((prev) =>
          prev.map((msg, index) =>
            index === prev.length - 1
              ? {
                  ...msg,
                  text: "An error occurred. Please try again.",
                  loading: false,
                }
              : msg
          )
        );
      }
    }
  };

  const displayMessageLetterByLetter = (message) => {
    setCurrentMessage("");
    setLetterIndex(0);
    setIsDisplayingMessage(true);
    const interval = setInterval(() => {
      setLetterIndex((prevIndex) => {
        const newIndex = prevIndex + 5;
        if (newIndex >= message.length) {
          clearInterval(interval);
          setIsDisplayingMessage(false);
        }
        return newIndex;
      });
    }, 50);
  };

  useEffect(() => {
    if (isDisplayingMessage) {
      const message = messages[messages.length - 1].text;
      setCurrentMessage(message.slice(0, letterIndex));
    }
  }, [letterIndex, isDisplayingMessage, messages]);

  const renderStaticMessage = (message) => {
    return (
      <div className="com_pass_ai_bx">
        <div
          className="com_pass_ai"
          dangerouslySetInnerHTML={{
            __html: message,
          }}
        ></div>
        {/* <button onClick={toggleMessageExpansion} className="view-more-less-btn">
          {isMessageExpanded ? "View Less" : "View More"}
          <img
            src={isMessageExpanded ? Aidropdownup : Aidropdown}
            alt="Aidropdown"
          />
        </button> */}
      </div>
    );
  };

  return (
    <div className="wrapper-main">
      <div className="wrapper-chatbot">
        <div className="chatbot-container">
          <div className="chatbot-header justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <img
                className="chatbot-logo"
                src={oscarLogo}
                alt="Oscar"
                width="38"
                height="38"
              />
              <h6>Oscar - Your Assistant</h6>
            </div>

            <div className="header-buttons">
              {/* <button onClick={handleRefresh} className="refresh-button">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  version="1.1"
                  viewBox="0 0 32 32"
                  width="14"
                  height="14"
                >
                  <g>
                    <path
                      d="M16 32A16 16 0 1 1 27.733 5.122a1 1 0 1 1-1.466 1.359A14 14 0 1 0 30 16a1 1 0 0 1 2 0 16.019 16.019 0 0 1-16 16z"
                      fill="#e55426"
                    />
                    <path
                      d="M27 7a1 1 0 0 1-1-1V1a1 1 0 0 1 2 0v5a1 1 0 0 1-1 1z"
                      fill="#e55426"
                    />
                    <path
                      d="M27 7h-5a1 1 0 0 1 0-2h5a1 1 0 0 1 0 2z"
                      fill="#e55426"
                    />
                  </g>
                </svg>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  version="1.1"
                  width="25"
                  height="25"
                  x="0"
                  y="0"
                  viewBox="0 0 24 24"
                  style={{ enableBackground: "new 0 0 512 512" }}
                  xmlSpace="preserve"
                  className=""
                >
                  <g>
                    <path
                      d="M19.602 12.635a.99.99 0 0 0-1.134.844 6.402 6.402 0 0 1-1.83 3.618 6.506 6.506 0 0 1-9.192 0 6.507 6.507 0 0 1 0-9.192 6.414 6.414 0 0 1 3.503-1.8 6.187 6.187 0 0 1 1.848-.055 6.414 6.414 0 0 1 2.466.828l-1.302.223a1 1 0 1 0 .338 1.971l3.49-.596a1.002 1.002 0 0 0 .816-1.155l-.597-3.49a1 1 0 1 0-1.97.338l.156.919a8.423 8.423 0 0 0-3.17-1.025 8.102 8.102 0 0 0-2.428.074 8.379 8.379 0 0 0-4.564 2.354c-3.313 3.314-3.313 8.705 0 12.02a8.471 8.471 0 0 0 6.01 2.485 8.471 8.471 0 0 0 6.01-2.485 8.39 8.39 0 0 0 2.394-4.741 1 1 0 0 0-.843-1.135z"
                      fill="#e55426"
                      opacity="1"
                    />
                  </g>
                </svg>

              </button> */}
              <button onClick={handleClose} className="close-button">
                {/* <svg className="close_icon" xmlns="http://www.w3.org/2000/svg" version="1.1" xmlns:xlink="http://www.w3.org/1999/xlink" width="22" height="22" x="0" y="0" viewBox="0 0 64 64" style="enable-background: new 0 0 512 512" xml:space="preserve">
                            <g>
                                <path d="M4.59 59.41a2 2 0 0 0 2.83 0L32 34.83l24.59 24.58a2 2 0 0 0 2.83-2.83L34.83 32 59.41 7.41a2 2 0 0 0-2.83-2.83L32 29.17 7.41 4.59a2 2 0 0 0-2.82 2.82L29.17 32 4.59 56.59a2 2 0 0 0 0 2.82z" fill="#555555" opacity="1" data-original="#000000" className=""></path>
                            </g>
                        </svg> */}
                {/* <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 64 64"
                  width="14"
                  height="14"
                >
                  <g>
                    <path
                      d="M4.59 59.41a2 2 0 0 0 2.83 0L32 34.83l24.59 24.58a2 2 0 0 0 2.83-2.83L34.83 32 59.41 7.41a2 2 0 0 0-2.83-2.83L32 29.17 7.41 4.59a2 2 0 0 0-2.82 2.82L29.17 32 4.59 56.59a2 2 0 0 0 0 2.82z"
                      fill="#e55426"
                    />
                  </g>
                </svg> */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  version="1.1"
                  width="17"
                  height="17"
                  x="0"
                  y="0"
                  viewBox="0 0 329.269 329"
                  style={{ enableBackground: "new 0 0 512 512" }}
                  xmlSpace="preserve"
                  className=""
                >
                  <g>
                    <path
                      d="M194.8 164.77 323.013 36.555c8.343-8.34 8.343-21.825 0-30.164-8.34-8.34-21.825-8.34-30.164 0L164.633 134.605 36.422 6.391c-8.344-8.34-21.824-8.34-30.164 0-8.344 8.34-8.344 21.824 0 30.164l128.21 128.215L6.259 292.984c-8.344 8.34-8.344 21.825 0 30.164a21.266 21.266 0 0 0 15.082 6.25c5.46 0 10.922-2.09 15.082-6.25l128.21-128.214 128.216 128.214a21.273 21.273 0 0 0 15.082 6.25c5.46 0 10.922-2.09 15.082-6.25 8.343-8.34 8.343-21.824 0-30.164zm0 0"
                      fill="#e55426"
                      opacity="1"
                    />
                  </g>
                </svg>
              </button>
            </div>
          </div>

          <div className="chatbot-container chatbot-inner">
            {messages.map((msg, index) =>
              msg.type === "bot" ? (
                <div key={msg + index} className="bot-parent">
                  <div className="oscar-logo_chat">
                    <img
                      src={oscarLogo}
                      alt="oscar-chat"
                      width="30"
                      height="30"
                    />
                  </div>
                  {msg.loading ? (
                    <div className="loader"></div>
                  ) : (
                    <div className="message bot-message padding-0">
                      <img src="/Oscar.svg" className="d-none" alt="" />
                      {index === 0 && !msg.option
                        ? renderStaticMessage(msg.text)
                        : isDisplayingMessage && index === messages.length - 1
                        ? StingKillInfo(currentMessage)
                        : StingKillInfo(msg.text)}
                    </div>
                  )}
                </div>
              ) : (
                <div key={msg + index} className="user-message-parent">
                  <div className="message user-message padding-0">
                    {StingKillInfo(msg.text)}
                  </div>
                </div>
              )
            )}
            <div ref={messagesEndRef} style={{ height: "1px" }} />
            {/* <ins class="adsbygoogle"
            style={{display:"block"}}
            data-ad-client="ca-pub-7261460138611909"
            data-ad-slot="5072169754"
            data-ad-format="auto"
            data-full-width-responsive="true">
          </ins>  */}
            <GoogleAd
              adClient={process.env.REACT_APP_GOOGLE_AD_CLIENT}
              adSlot={process.env.REACT_APP_GOOGLE_AD_SLOT}
            />
          </div>

          <div className="chat-input-container">
            <div className="input-innerwrapper">
              <textarea
                ref={textareaRef}
                className="chat-input"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder="Ask Oscar your questions here"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                disabled={messages.some((msg) => msg.loading)}
              ></textarea>
              <div className="chat-bot-logo">
                <img
                  src={oscarLogo}
                  alt="input-logo"
                  width="30"
                  height="30"
                ></img>
              </div>
            </div>

            <button
              className="send-button"
              onClick={handleSubmit}
              disabled={messages.some((msg) => msg.loading)}
            >
              <svg width="22" height="22" viewBox="0 0 25 25">
                <path
                  d="M22.65 12 2.76 3a.53.53 0 0 0-.55.09.5.5 0 0 0-.13.54l3.26 8.87-3.26 8.83a.5.5 0 0 0 .13.54.55.55 0 0 0 .34.13.52.52 0 0 0 .21 0l19.89-9a.5.5 0 0 0 0-.92z"
                  fill="#e55426"
                ></path>
              </svg>
            </button>
          </div>

          {/* <button className="send-button" onClick={handleSubmit}>
            <svg width="22" height="22" viewBox="0 0 25 25">
              <path
                d="M22.65 12 2.76 3a.53.53 0 0 0-.55.09.5.5 0 0 0-.13.54l3.26 8.87-3.26 8.83a.5.5 0 0 0 .13.54.55.55 0 0 0 .34.13.52.52 0 0 0 .21 0l19.89-9a.5.5 0 0 0 0-.92z"
                fill="#e55426"
              ></path>
            </svg>
          </button> */}
        </div>

        {/* <a href="" className="btn-oscar" data-bs-toggle="modal" data-bs-target="#chatbot">
  <img src={oscarLogo} alt="oscar-logo" width="55" height="55"></img>
</a>


<div className="modal fade" id="chatbot" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
  <div className="modal-dialog modal-dialog-centered">
    <div className="modal-content">
      <div className="modal-header">
        <h1 className="modal-title fs-5" id="exampleModalLabel">Modal title</h1>
        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div className="modal-body">
        ...
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        <button type="button" className="btn btn-primary">Save changes</button>
      </div>
    </div>
  </div>
</div> */}
      </div>
    </div>
  );
}

export default Chatbot;
